import { useMaybeRoomContext } from '@livekit/components-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { EventEmitter } from 'events';
import { useEventLogger } from 'FrontRoyalAngular';
import { type AudioUiContext, useVoiceAgentStatsigConfig } from 'TutorBotConversation';
import { type AnyObject } from '@Types';

function useLogEventOnce() {
    const EventLogger = useEventLogger();
    const loggedEvents = useRef<string[]>([]);

    const logOnce = useCallback(
        (eventType: string, payload: AnyObject) => {
            if (loggedEvents.current.includes(eventType)) return;
            EventLogger.log(eventType, payload);
            loggedEvents.current.push(eventType);
        },
        [EventLogger, loggedEvents],
    );

    return { logOnce };
}

export function useSessionTimer({ uiContext }: { uiContext: AudioUiContext }) {
    const { logOnce } = useLogEventOnce();
    const room = useMaybeRoomContext();
    const sessionTimer = useMemo(
        () =>
            new EventEmitter<{
                sessionWillEndSoon: [{ secondsRemaining: number }];
            }>(),
        [],
    );
    const sessionStartTime = useMemo(() => new Date(), []);
    const { sessionTimeLimitSeconds, sessionEndWarningSeconds } = useVoiceAgentStatsigConfig(uiContext);

    // return an EventDispatcher that triggers 'sessionWillEndSoon' once each second between sessionTimeLimitSeconds and sessionEndWarningSeconds,
    // and then 'forceSessionEnd' once at sessionEndWarningSeconds
    // Remember that this is a react hook
    useEffect(() => {
        const interval = setInterval(() => {
            const secondsRemaining =
                sessionTimeLimitSeconds - (new Date().getTime() - sessionStartTime.getTime()) / 1000;

            if (secondsRemaining >= 0 && secondsRemaining <= sessionEndWarningSeconds) {
                sessionTimer.emit('sessionWillEndSoon', { secondsRemaining });
                logOnce('tutorbot:voice_session_will_end_soon', {
                    value: sessionTimeLimitSeconds,
                    label: uiContext,
                    ui_context: uiContext,
                });
            }
            if (secondsRemaining <= 0) {
                room?.disconnect();
                logOnce('tutorbot:voice_session_timed_out', {
                    value: sessionTimeLimitSeconds,
                    label: uiContext,
                    ui_context: uiContext,
                });
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [sessionTimeLimitSeconds, sessionEndWarningSeconds, sessionStartTime, sessionTimer, room, logOnce, uiContext]);

    return sessionTimer;
}
